import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import ChatLauncher from './Chat/ChatLauncher';

interface DarkLayoutProps {
  children: React.ReactNode;
  title: string;
  /** Optional subtitle shown below the title (e.g. "Estimate #EST-042") */
  subtitle?: string;
  /** Optional left-side content (e.g. back button). When set, shown before title. */
  headerLeft?: React.ReactNode;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  /** Optional right-side content for the main header (e.g. primary action button) */
  headerRight?: React.ReactNode;
}

const DarkLayout: React.FC<DarkLayoutProps> = ({
  children,
  title,
  subtitle,
  headerLeft,
  activeTab = 'users',
  onTabChange,
  headerRight,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/logout');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', href: '/dashboard' },
    { id: 'users', label: 'User Management', icon: 'group', href: '/users', requiredPermission: permissions.userManagement.view },
    { id: 'projects', label: 'Projects', icon: 'folder', href: '/projects', requiredPermission: permissions.projects.view },
    { id: 'costlibrary', label: 'Cost Library', icon: 'inventory', href: '/costlibrary', requiredPermission: permissions.costLibrary.view },
    { id: 'masterdata', label: 'Master Data', icon: 'database', href: '/masterdata', requiredPermission: permissions.masterData.view },
    { id: 'documentarchives', label: 'Document Archives', icon: 'archive', href: '/document-archives', requiredPermission: permissions.documentArchives.view },
    { id: 'settings', label: 'Settings', icon: 'settings', href: '/settings' },
    { id: 'support', label: 'Support', icon: 'help', href: '/support' },
  ];

  const visibleNavigationItems = navigationItems.filter((item) =>
    !('requiredPermission' in item) || !item.requiredPermission || hasPermission(user, item.requiredPermission)
  );

  const isActive = (item: (typeof navigationItems)[0]) =>
    activeTab === item.id ||
    (item.id === 'users' && activeTab === 'users') ||
    (item.id === 'costlibrary' && location.pathname === '/costlibrary') ||
    (item.id === 'masterdata' && location.pathname === '/masterdata') ||
    (item.id === 'projects' && location.pathname.startsWith('/projects')) ||
    (item.id === 'documentarchives' && location.pathname === '/document-archives');

  const handleNavigation = (item: (typeof navigationItems)[0]) => {
    if (item.id === 'users' && onTabChange && location.pathname === '/users') {
      onTabChange('users');
    } else {
      navigate(item.href);
    }
    setSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-background-dark text-slate-200 font-display text-base overflow-x-hidden">
      {/* Backdrop - closes sidebar when clicked */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setSidebarOpen(false)}
        onKeyDown={(e) => e.key === 'Enter' && setSidebarOpen(false)}
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity ${sidebarOpen ? 'block' : 'hidden'}`}
        aria-label="Close menu"
      />

      {/* Sidebar - template style */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col bg-sidebar-dark border-r border-border-dark transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo / Brand - template: accent-blue icon, text-xl font-bold */}
        <div className="p-5 flex items-center gap-3 shrink-0">
          <div className="text-accent-blue flex size-10 shrink-0 items-center justify-center bg-accent-blue/10 rounded-lg">
            <span className="material-symbols-outlined text-xl">architecture</span>
          </div>
          <span className="text-white text-xl font-bold tracking-tight">ArchiFlow</span>
        </div>

        {/* Navigation - template: font-semibold active, font-medium inactive, text-sm equivalent via py-3 */}
        <nav className="flex-1 px-4 space-y-2 mt-4 overflow-y-auto">
          {visibleNavigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item)}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl w-full text-left transition-all text-sm ${
                isActive(item)
                  ? 'bg-primary/10 text-primary border border-primary/20 font-semibold'
                  : 'text-slate-400 hover:bg-white/5 hover:text-white border border-transparent font-medium'
              }`}
            >
              <span className={`material-symbols-outlined text-xl ${isActive(item) ? 'fill-1' : ''}`}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User + Logout - template: text-xs font-bold name, text-[10px] uppercase meta, text-sm font-semibold logout */}
        <div className="mt-auto p-4 border-t border-border-dark shrink-0">
          <div className="flex items-center gap-3 p-2">
            <div className="size-9 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold shrink-0 text-white">
              {(user?.username || 'A').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-white truncate">{user?.username || 'Admin'}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider truncate">
                {user?.email || 'admin@example.com'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-3 flex w-full items-center gap-3 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm font-semibold"
          >
            <span className="material-symbols-outlined text-xl">logout</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content - template: header text-xl font-bold leading-tight tracking-tight */}
      <main className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center bg-elevation-dark p-4 lg:px-5 border-b border-border-dark sticky top-0 z-30 justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Open menu"
              className="flex items-center justify-center size-10 rounded-lg hover:bg-white/5 text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="material-symbols-outlined text-xl">menu</span>
            </button>
            {headerLeft}
            <div>
              <h2 className="text-white text-xl font-bold leading-tight tracking-tight">{title}</h2>
              {subtitle && (
                <p className="text-slate-400 text-xs font-medium">{subtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center">{headerRight}</div>
        </div>

        {/* Page content */}
        <div className="flex-1 p-0 text-slate-200 my-[10px]">{children}</div>
      </main>

      {/* Always-visible chat launcher */}
      <ChatLauncher />
    </div>
  );
};

export default DarkLayout;
